import { AxiosError } from "axios";
import {
  AIBillingDashboardResponse,
  AIBillingErrorKind,
  AIBillingExportRequest,
  AIBillingPagedRequest,
  AIBillingPaymentListResponse,
  AIBillingPeriodRequest,
  AIBillingUsageResponse
} from "../entities/company/AIBilling/AIBilling";
import RequestService from "./RequestService";

export * from "../entities/company/AIBilling/AIBilling";

export const AI_BILLING_DEFAULT_PAGE_SIZE = 25;

export function getErrorKind(result: unknown): AIBillingErrorKind {
  if (!(result instanceof Error)) {
    return "none";
  }
  return (result as AxiosError).response?.status === 403
    ? "forbidden"
    : "generic";
}

type QueryValue = string | number | undefined;

const buildQueryString = (params: Record<string, QueryValue>): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      query.append(key, String(value));
    }
  });
  return query.toString();
};

const buildSortableQuery = (
  request: AIBillingExportRequest
): Record<string, QueryValue> => ({
  from: request.from,
  to: request.to,
  sortColumn: request.sortColumn,
  descending:
    request.descending === undefined ? undefined : Number(request.descending)
});

const buildPagedQuery = (request: AIBillingPagedRequest): string =>
  buildQueryString({
    ...buildSortableQuery(request),
    page: request.page,
    pageSize: request.pageSize
  });

const buildExportQuery = (request: AIBillingExportRequest): string =>
  buildQueryString(buildSortableQuery(request));
class AIBillingService {
  public static getDashboard(
    request: AIBillingPeriodRequest
  ): Promise<AIBillingDashboardResponse | Error> {
    const query = buildQueryString({ from: request.from, to: request.to });
    return RequestService.get(
      `/aibilling/dashboard?${query}`,
      {},
      true,
      RequestService.getBaseUrlAssistance()
    );
  }

  public static getUsage(
    request: AIBillingPagedRequest
  ): Promise<AIBillingUsageResponse | Error> {
    return RequestService.get(
      `/aibilling/usage?${buildPagedQuery(request)}`,
      {},
      true,
      RequestService.getBaseUrlAssistance()
    );
  }

  public static exportUsage(
    request: AIBillingExportRequest
  ): Promise<Error | unknown> {
    return RequestService.downloadFile(
      `/aibilling/usage/export?${buildExportQuery(request)}`,
      "export.xlsx",
      RequestService.getBaseUrlAssistance()
    );
  }

  public static getPayments(
    request: AIBillingPagedRequest
  ): Promise<AIBillingPaymentListResponse | Error> {
    return RequestService.get(
      `/aibilling/payments?${buildPagedQuery(request)}`,
      {},
      true,
      RequestService.getBaseUrlAssistance()
    );
  }

  public static exportPayments(
    request: AIBillingExportRequest
  ): Promise<Error | unknown> {
    return RequestService.downloadFile(
      `/aibilling/payments/export?${buildExportQuery(request)}`,
      "export.xlsx",
      RequestService.getBaseUrlAssistance()
    );
  }
}

export default AIBillingService;
